/**
 * Page and Section Priority Configuration
 * Used for media loading optimization and preloading strategies
 * Higher number = higher priority
 */

// Media priority levels (matching Prisma enum)
export type MediaPriorityLevel = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW' | 'BACKGROUND';

export const PAGE_PRIORITY = {
  // Home page has highest priority
  '': 10, // Home page (slug: '')
  
  // Main navigation pages - high priority
  'about': 9,
  'benefits': 9,
  'services': 9,
  'contact': 9,
  
  // Action pages - medium-high priority
  'join-us': 8,
  
  // Authentication pages - lower priority
  'sign-in': 5,
} as const;

export type PageSlug = keyof typeof PAGE_PRIORITY | string;

/**
 * Section priority based on position in page
 * Hero (index 0) = CRITICAL
 * Above fold (index 1-2) = HIGH
 * Mid-page (index 3-5) = NORMAL
 * Below fold (index 6+) = LOW
 */
export const SECTION_PRIORITY_THRESHOLDS = {
  CRITICAL: 0,  // Hero section only
  HIGH: 3,      // First 3 sections (above fold)
  NORMAL: 6,    // Sections 3-5
  LOW: Infinity // Everything else
} as const;

/**
 * Get priority for a page slug
 * @param slug Page slug (empty string for home page)
 * @returns Priority number (higher = more important)
 */
export function getPagePriority(slug: string): number {
  if (slug in PAGE_PRIORITY) {
    return PAGE_PRIORITY[slug as keyof typeof PAGE_PRIORITY];
  }
  // Default priority for unknown pages
  return 3;
}

/**
 * Get media priority level based on section index
 * @param sectionIndex 0-based index of section in page
 * @returns MediaPriorityLevel
 */
export function getSectionMediaPriority(sectionIndex: number): MediaPriorityLevel {
  if (sectionIndex === 0) return 'CRITICAL';
  if (sectionIndex < SECTION_PRIORITY_THRESHOLDS.HIGH) return 'HIGH';
  if (sectionIndex < SECTION_PRIORITY_THRESHOLDS.NORMAL) return 'NORMAL';
  return 'LOW';
}

/**
 * Get preload strategy based on priority
 */
export function getPreloadStrategy(priority: MediaPriorityLevel): {
  preload: boolean;
  fetchPriority: 'high' | 'low' | 'auto';
  loading: 'eager' | 'lazy';
} {
  switch (priority) {
    case 'CRITICAL':
      return { preload: true, fetchPriority: 'high', loading: 'eager' };
    case 'HIGH':
      return { preload: true, fetchPriority: 'high', loading: 'eager' };
    case 'NORMAL':
      return { preload: false, fetchPriority: 'auto', loading: 'lazy' };
    case 'LOW':
    case 'BACKGROUND':
    default:
      return { preload: false, fetchPriority: 'low', loading: 'lazy' };
  }
}

/**
 * Sort pages by priority (highest first)
 */
export function sortPagesByPriority<T extends { slug: string }>(pages: T[]): T[] {
  return [...pages].sort((a, b) => {
    const priorityA = getPagePriority(a.slug);
    const priorityB = getPagePriority(b.slug);
    return priorityB - priorityA; // Descending order (higher priority first)
  });
}

/**
 * Get ordered list of page slugs by priority
 */
export function getPageSlugsByPriority(): string[] {
  return Object.entries(PAGE_PRIORITY)
    .sort(([, a], [, b]) => b - a) // Sort by priority descending
    .map(([slug]) => slug);
}

/**
 * Get pages to prefetch based on current page
 * Returns pages that should be prefetched for navigation
 */
export function getPagesToPreload(currentSlug: string): string[] {
  const allSlugs = getPageSlugsByPriority();
  // Always include home page and main nav pages
  const mainNavSlugs = ['', 'about', 'services', 'benefits', 'contact'];
  
  // Filter out current page and return unique slugs
  return [...new Set([...mainNavSlugs, ...allSlugs])]
    .filter(slug => slug !== currentSlug);
}

